import { Injectable } from '@angular/core';
import { v4 as uuid } from 'uuid';
import { getDownloadURL, getStorage, ref, StorageError, UploadTask } from '@angular/fire/storage';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { IResource, ResourceItemModel } from '../models/resource';
import { ERRORS } from '../common/utils/notification-constants';
import {
  ComputerFile,
  UPLOAD_LIMITS,
  UPLOAD_METHOD,
  UploadedFile,
} from '../dialogs/upload/upload/upload.component';
import { SpaceRepository } from '../state/space.repository';
import { FileDetails } from '../content/upload-task/upload-task.component';
import { BoardItemsManager } from '../sessions/session/items-canvas/board-items/board-items-manager';
import { resourceItemModelToItemModel } from '../sessions/session/items-canvas/board-items/board-items-factory';
import { ConfirmationModalComponent } from '../standalones/components/modals/confirmation-modal/confirmation-modal.component';
import { NotificationToasterService } from './notification-toaster.service';

import { TelemetryService } from './telemetry.service';
import { UserService } from './user.service';
import { SpaceBoardsService } from './space-boards.service';
import { SessionSharedDataService } from './session-shared-data.service';
import { UploadFile } from './document-upload.service';
import { CdnResourcesSignerService } from './cdn-resources-signer.service';
import { FLAGS, FlagsService } from './flags.service';
import { HEIC_HEIF_ACCEPT_LIST, isImageFile } from './image-file-type.utils';
import { FirebaseStorageUploadService } from './firebase-storage-upload';

export { HEIC_HEIF_ACCEPT_LIST, imageNeedsConversion, isImageFile } from './image-file-type.utils';

// Maximum allowed size upload - 25 MBs
export const MAX_FILE_SIZE_ALLOWED_MB = 25;
// Maximum upload retry time is 60 secs
const MAX_UPLOAD_RETRY_TIME = 60 * 1000;

export enum UploadFailedNotificationStatus {
  SYSTEM_FAILURE = 'SYSTEM_FAILURE',
  CANNOT_CREATE_BOARDS = 'CANNOT_CREATE_BOARDS',
  SIZE_LIMIT = 'SIZE_LIMIT',
  VIMEO_UPLOAD_FAILED = 'VIMEO_UPLOAD_FAILED',
  UNSUPPORTED_FILE = 'UNSUPPORTED_FILE',
  IMAGE_CONVERSION_FAILED = 'IMAGE_CONVERSION_FAILED',
}

// OneDrive
export const ACCEPTED_ONEDRIVE_TYPES = [
  '.docx',
  '.doc',
  '.pptx',
  '.ppt',
  '.odp',
  '.odt',
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.svg',
  '.tif',
  '.tiff',
  '.mp4',
  '.webm',
  '.hwp',
  '.hwpx',
  '.heic',
  '.heif',
];

// LibreOffice PDF Converter
export const ACCEPTED_LIBREOFFICE_MIME_TYPES = [
  'application/msword',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/x-hwp',
  'application/x-hwpx',
];

export const ACCEPTED_LIBREOFFICE_EXTENSIONS = ['.hwp', '.hwpx'];

export const SUPPORTED_UPLOAD_ACCEPT_TYPES = [
  'application/pdf',
  ...ACCEPTED_LIBREOFFICE_MIME_TYPES,
  ...ACCEPTED_LIBREOFFICE_EXTENSIONS,
  'image/*',
  HEIC_HEIF_ACCEPT_LIST,
  'video/*',
  '.space',
].join(', ');

export function checkIfValidLibreOfficeType(file: File) {
  return (
    ACCEPTED_LIBREOFFICE_MIME_TYPES.some((s) => file.type.startsWith(s)) ||
    ACCEPTED_LIBREOFFICE_EXTENSIONS.some((s) => file.name.endsWith(s))
  );
}

export function resolveDocumentMimeType(file: File) {
  if (['', 'application/octet-stream'].includes(file.type)) {
    if (file.name.toLowerCase().endsWith('.hwpx')) {
      return new File([file], file.name, { type: 'application/x-hwpx' });
    } else if (file.name.toLowerCase().endsWith('.hwp')) {
      return new File([file], file.name, { type: 'application/x-hwp' });
    } else {
      return file;
    }
  } else {
    return file;
  }
}

export function getAcceptedLibreOfficePickerTypes() {
  return `${ACCEPTED_LIBREOFFICE_MIME_TYPES.concat(ACCEPTED_LIBREOFFICE_EXTENSIONS).join(', ')}`;
}

export interface GroupUploadData {
  uploadId: string;
  totalFiles: number;
  uploadedFilesCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class UploadFileService {
  readonly uploadTasksProgressObservables$ = new BehaviorSubject<Record<string, any>>({});
  readonly uploadedFiles$ = new BehaviorSubject<Record<string, File | UploadFile | undefined>>({});
  private readonly _pendingLeftPanelFile$ = new BehaviorSubject<File | undefined>(undefined);
  readonly pendingLeftPanelFile$ = this._pendingLeftPanelFile$.asObservable();
  readonly groupUploadProgress$ = new BehaviorSubject<Record<string, GroupUploadData>>({});

  private _fileUploadsInProgress = 0;

  uploadedFiles: Record<string, File | UploadFile | undefined> = {};
  uploadTasksCancellationFunctions: Record<string, () => any | undefined> = {};
  uploadIdsMap: Record<string, string> = {};
  uploadTasksProgressObservables: Record<string, any> = {};

  static checkFileType(file: FileDetails | File): ResourceItemModel {
    const name = 'name' in file ? file.name : '';
    const type = file.type ?? '';
    if (type.startsWith('video/')) {
      return ResourceItemModel.VIDEO;
    }
    if (isImageFile({ name: name ?? '', type })) {
      return ResourceItemModel.IMAGE;
    }
    return ResourceItemModel.DOCUMENT;
  }

  constructor(
    private translateService: TranslateService,
    private notificationToasterService: NotificationToasterService,
    private telemetry: TelemetryService,
    private userService: UserService,
    private spaceRepo: SpaceRepository,
    private spaceBoardsService: SpaceBoardsService,
    private sharedDataService: SessionSharedDataService,
    private matDialog: MatDialog,
    private flagsService: FlagsService,
    private cdnResourcesSignerService: CdnResourcesSignerService,
    private firebaseStorageUploadService: FirebaseStorageUploadService,
  ) {}

  uploadBase64String(base64String: string, fileName: string) {
    const text = atob(base64String);
    const blob = new Blob([text], { type: 'text/plain' });
    const file = new File([blob], fileName);
    const storage = getStorage();
    const uploadRef = ref(storage, fileName);
    const uploadTask = this.firebaseStorageUploadService.uploadBytesResumable(uploadRef, file);
    uploadTask.catch((error) => this.handleError(error));
  }

  uploadToFireStorage(
    file: File,
    onSuccess: (url: string, fileName?: string) => void,
    onFailure?: () => void,
    appendFileNameToId = false,
    maxFileSizeInMB = MAX_FILE_SIZE_ALLOWED_MB,
    expectedResourceType?: ResourceItemModel,
  ): UploadTask | undefined {
    if (file.size > maxFileSizeInMB * 1024 * 1024) {
      this.notificationToasterService.showDefaultErrorNotificationV2(
        this.translateService.instant('Upload Failed'),
        `${this.translateService.instant(
          'File exceeds maximum upload limit',
        )} ${maxFileSizeInMB} MBs`,
        ERRORS.UPLOAD_FAILED,
      );
      return undefined;
    }

    if (expectedResourceType) {
      const actualFileType = UploadFileService.checkFileType(file);
      if (actualFileType !== expectedResourceType) {
        this.notificationToasterService.showDefaultErrorNotificationV2(
          this.translateService.instant('Upload Failed'),
          this.translateService.instant('Invalid file type.'),
          ERRORS.UPLOAD_FAILED,
        );
        return undefined;
      }
    }

    const id = uuid() + (appendFileNameToId ? file.name : '');

    const storage = getStorage();
    storage.maxUploadRetryTime = MAX_UPLOAD_RETRY_TIME;
    const uploadRef = ref(storage, id);

    const uploadTask = this.firebaseStorageUploadService.uploadBytesResumable(uploadRef, file);
    uploadTask.catch((error) => {
      this.handleError(error);
      if (onFailure) {
        onFailure();
      }
    });
    uploadTask.then(async () => {
      const url = await this.resolveDownloadUrl(uploadRef);
      if (url.includes('base64')) {
        this.telemetry.errorEvent('corrupted_image', {
          fileName: file.name,
          fileSize: file.size,
          userId: this.userService.userId,
          content_id: id,
          reason: 'firebase_url',
          sessionId: this.spaceRepo.activeSpaceId,
        });
        this.uploadBase64String(url, `corrupted_images/${id}`);
        // A corrupted upload is a failure for the caller; previously neither
        // callback fired here, leaving callers waiting forever.
        onFailure?.();
        return;
      }

      onSuccess(url, id);
    });
    return uploadTask;
  }

  private async resolveDownloadUrl(uploadRef: ReturnType<typeof ref>): Promise<string> {
    if (!this.flagsService.isFlagEnabled(FLAGS.ENABLE_CDN_RESOURCES_SIGNING)) {
      return getDownloadURL(uploadRef);
    }

    try {
      return await this.cdnResourcesSignerService.getSignedUrl(uploadRef.fullPath);
    } catch (error) {
      return getDownloadURL(uploadRef);
    }
  }

  showUploadFailedNotification(reason?: UploadFailedNotificationStatus) {
    let messageText;
    if (reason && reason === UploadFailedNotificationStatus.SYSTEM_FAILURE) {
      messageText = this.translateService.instant(
        'Upload failed due to a system error. Please try again.',
      );
    } else if (reason && reason === UploadFailedNotificationStatus.VIMEO_UPLOAD_FAILED) {
      messageText = this.translateService.instant('Vimeo upload failed. Please try again.');
    } else if (reason && reason === UploadFailedNotificationStatus.SIZE_LIMIT) {
      messageText = this.translateService.instant('Cannot upload files larger than {{size}}MBs', {
        size: UPLOAD_LIMITS.DOCUMENT.SIZE,
      });
    } else if (reason && reason === UploadFailedNotificationStatus.UNSUPPORTED_FILE) {
      messageText = this.translateService.instant(
        'This file type is not supported. Please select a supported file format.',
      );
    } else if (reason && reason === UploadFailedNotificationStatus.IMAGE_CONVERSION_FAILED) {
      messageText = this.translateService.instant(
        'Some images could not be prepared for display. Please try again or use JPEG or PNG.',
      );
    } else {
      messageText = this.translateService.instant(
        'Upload failed due to a network issue. Please try again.',
      );
    }
    this.notificationToasterService.showDefaultErrorNotificationV2(
      this.translateService.instant('Upload failed'),
      messageText,
      ERRORS.UPLOAD_FAILED,
    );
  }

  private handleError(error: StorageError) {
    switch (error.code) {
      case 'storage/canceled':
        break;
      default:
        this.showUploadFailedNotification();
    }
  }

  public extractFileType(f: File): ResourceItemModel {
    if (isImageFile(f)) {
      return ResourceItemModel.IMAGE;
    } else if (f.type.startsWith('video/')) {
      return ResourceItemModel.VIDEO;
    } else if (f.type.startsWith('application/pdf') || checkIfValidLibreOfficeType(f)) {
      return ResourceItemModel.DOCUMENT;
    } else {
      return ResourceItemModel.ANY;
    }
  }

  public containsMoreThanOneType(files: File[], type: string): boolean {
    const filesOfType = files.filter((file) => file.type === type);

    return filesOfType.length > 1;
  }

  public AllowOneFileFromType(files: File[], type: string): File[] {
    let foundType = false;

    return files.filter((file) => {
      if (file.type === type && !foundType) {
        foundType = true;
        return true; // Keep the first file of the specified type
      }

      return false; // Filter out other files of the same type
    });
  }

  public handleUpload(result: UploadedFile): void {
    if (!this.spaceRepo.activeSpace || !result) {
      return;
    }
    const frameUid =
      this.spaceRepo.activeSpace.selectedBoardUid ??
      this.spaceBoardsService.getActiveSpaceCurrentRoomAccessibleBoards()?.[0].uid; // default to first frame

    if (result.uploadMethod === UPLOAD_METHOD.PENCIL_LIBRARY) {
      let items = (result.files as IResource[]).map((file) => {
        if (file.url && file.url.includes('base64')) {
          this.telemetry.errorEvent('corrupted_image', {
            reason: 'pencil_cloud',
            user: this.userService.userId,
            spaceId: this.spaceRepo.activeSpaceId,
          });
          return;
        }
        return { url: file.url, type: resourceItemModelToItemModel(file.type) };
      });
      items = items.filter((item) => !!item);
      items.forEach((item) => {
        if (!item) {
          return;
        }
        const { url, type } = item;
        BoardItemsManager.instance?.insertNewBoardItem(
          type,
          {
            url,
          },
          { renderInNearestFreeSpace: true },
        );
      });
    } else if (result.uploadMethod === UPLOAD_METHOD.COMPUTER) {
      const files: UploadFile[] = [];
      for (const file of result.files as ComputerFile[]) {
        files.push(
          new UploadFile(
            file.file,
            file.file.name,
            file.file.size,
            file.type,
            this.spaceRepo.activeSpace._id,
            frameUid,
            { renderInNearestFreeSpace: true },
          ),
        );
      }
      this.sharedDataService.uploadItem.next(files);
    } else {
      const selectedFrameUid = frameUid;
      const url = (result.files as string[])[0];
      const fileName = this.fileNameFromURL(url);
      this.sharedDataService.uploadItem.next([
        new UploadFile(
          url,
          fileName,
          0,
          ResourceItemModel.VIDEO,
          this.spaceRepo.activeSpace._id,
          selectedFrameUid,
        ),
      ]);
    }
  }

  /*
   *  Get the name which is between the last ( / ) and the ( . ) before the extension
   * */
  fileNameFromURL(url: string) {
    if (!url) {
      return '';
    }
    const splitNameBySlashes = url.split('/');
    if (!splitNameBySlashes.length) {
      return '';
    }
    const textAfterLastSlash = splitNameBySlashes[splitNameBySlashes.length - 1];
    const textBeforePeriod = textAfterLastSlash?.split('.')[0];
    if (!textBeforePeriod) {
      return url;
    }
    return textBeforePeriod;
  }

  setPendingLeftPanelFile(pendingLeftPanelFile: File | undefined) {
    this._pendingLeftPanelFile$.next(pendingLeftPanelFile);
  }

  cancelUpload(uploadId: string): void {
    if (this.uploadTasksCancellationFunctions[uploadId]) {
      this.uploadTasksCancellationFunctions[uploadId]();
    }
    this.deleteUploadTasksObjects(uploadId);
  }

  onResourceCreated(
    resourceType: ResourceItemModel | undefined,
    contentId: string,
    uploadId: string,
  ) {
    if (resourceType === ResourceItemModel.VIDEO) {
      this.deleteUploadTasksObjects(uploadId);
    } else {
      this.uploadIdsMap[contentId] = uploadId;
    }
  }

  createUploadTaskObjects(
    uploadId: string,
    fileInput: File | UploadFile,
    percentageObservable: Observable<{ progress: number }>,
    cancelUpload: () => any,
  ) {
    this.uploadTasksCancellationFunctions = {
      ...this.uploadTasksCancellationFunctions,
      [uploadId]: cancelUpload,
    };
    if (this.isGroupUploadTask(fileInput)) {
      this.createGroupUploadProgressObject(fileInput);
    } else {
      this.uploadTasksProgressObservables = {
        ...this.uploadTasksProgressObservables,
        [uploadId]: percentageObservable,
      };
    }

    this.uploadedFiles = {
      ...this.uploadedFiles,
      [uploadId]: fileInput,
    };
    this.uploadTasksProgressObservables$.next(this.uploadTasksProgressObservables);
    this.uploadedFiles$.next(this.uploadedFiles);
  }

  isGroupUploadTask(fileInput: File | UploadFile): boolean {
    const uploadFileOptions = (fileInput as UploadFile)?.options;
    return uploadFileOptions?.groupUploadData !== undefined;
  }

  createGroupUploadProgressObject(fileInput: File | UploadFile) {
    const uploadFileOptions = (fileInput as UploadFile)?.options;
    if (!uploadFileOptions || !uploadFileOptions.groupUploadData) {
      return;
    }
    const groupUploadData = uploadFileOptions.groupUploadData;
    const groupUploadId = groupUploadData.uploadId;
    if (!this.groupUploadProgress$.value[groupUploadId]) {
      this.groupUploadProgress$.next({
        ...this.groupUploadProgress$.value,
        [groupUploadId]: groupUploadData,
      });
    }
  }

  deleteUploadTasksObjects(uploadId: string) {
    delete this.uploadTasksCancellationFunctions[uploadId];
    this.uploadTasksCancellationFunctions = { ...this.uploadTasksCancellationFunctions };

    delete this.uploadTasksProgressObservables[uploadId];
    this.uploadTasksProgressObservables = { ...this.uploadTasksProgressObservables };

    delete this.uploadedFiles[uploadId];
    this.uploadedFiles = { ...this.uploadedFiles };

    this.uploadedFiles$.next(this.uploadedFiles);
    this.uploadTasksProgressObservables$.next(this.uploadTasksProgressObservables);
  }

  // TODO: make the parameter type safe (blocked on the refactoring of items-canvas.component)
  updateGroupUploadProgress(options: any) {
    if (!options || !options.groupUploadData) {
      return;
    }

    const notificationGroup: GroupUploadData = options.groupUploadData;

    const groupProgresses = { ...this.groupUploadProgress$.getValue() };

    const groupUploadId = notificationGroup.uploadId;

    const groupProgress = groupProgresses[groupUploadId];
    if (!groupProgress) {
      return;
    }

    groupProgress.uploadedFilesCount = groupProgress.uploadedFilesCount + 1;

    if (groupProgress.uploadedFilesCount === groupProgress.totalFiles) {
      delete groupProgresses[groupUploadId];
    }

    this.groupUploadProgress$.next(groupProgresses);
  }

  incrementUploadCounter() {
    this._fileUploadsInProgress++;
  }

  decrementUploadCounter() {
    this._fileUploadsInProgress--;
    if (this._fileUploadsInProgress < 0) {
      this._fileUploadsInProgress = 0;
      console.error('_fileUploadsInProgress unexpectedly moved below zero');
    }
  }

  resetUploadingCounter() {
    this._fileUploadsInProgress = 0;
  }

  isFileUploadInProgress() {
    return this._fileUploadsInProgress !== 0;
  }

  confirmLeavingSpaceWithUploadInProgress(): Promise<boolean> {
    if (!this.isFileUploadInProgress()) {
      return Promise.resolve(true);
    }
    return new Promise((resolve) => {
      this.matDialog
        .open(ConfirmationModalComponent, {
          data: {
            title: this.translateService.instant('Leave Space?'),
            description: this.translateService.instant(
              'By leaving this Space, any upload in progress may not be saved. Are you sure?',
            ),
            confirmButtonConfig: {
              icon: 'arrow_back',
              iconType: 'mat-icon',
              label: this.translateService.instant('Leave Space'),
              danger: true,
            },
            cancelButtonConfig: {
              icon: 'close',
              iconType: 'mat-icon',
              label: this.translateService.instant('Cancel'),
            },
          },
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe((output: 'confirm' | 'cancel') => {
          resolve(output === 'confirm');
        });
    });
  }
}
